/**
 * @file inward-tabs.tsx
 * @module app/modules/inward/components/inward-tabs
 * @description Switch between the Inward purchase orders and the Inward GRNs (approvals).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth";
import { canPerformInwardAction, InwardAction } from "../utils/inward-access";

interface InwardTabsProps {
  basePath: string;
  active: "pos" | "grns" | "report";
}

const InwardTabs = ({ basePath, active }: InwardTabsProps) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const tabs = [
    { key: "pos", label: "Purchase orders", path: basePath },
    { key: "grns", label: "GRNs & approvals", path: `${basePath}/grns` },
    ...(canPerformInwardAction(currentUser, InwardAction.DownloadReport)
      ? [
          {
            key: "report",
            label: "End-of-day report",
            path: `${basePath}/report`,
          },
        ]
      : []),
  ];
  return (
    <div
      className="inward-filters mb-4"
      role="tablist"
      aria-label="Inward sections"
    >
      {tabs.map((tab) => (
        <Button
          key={tab.key}
          role="tab"
          aria-selected={active === tab.key}
          variant={active === tab.key ? "dark" : "outline-dark"}
          className="inward-touch-btn"
          onClick={() => navigate(tab.path)}
          data-testid={`inward-tab-${tab.key}`}
        >
          {tab.label}
        </Button>
      ))}
    </div>
  );
};

export default InwardTabs;
